import React, { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  CircularProgress,
  Alert,
  Divider,
  List,
  ListItem,
  ListItemText
} from '@mui/material';
import { 
  migrateExistingClearingAccounts,
  createClearingAccountsForExistingMainAccounts,
  fixClearingAccountReferences,
  createDefaultAccountsReceivable
} from '../firebase/glAccountServices';

const AdminUtilities: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<{ success: boolean; message: string } | null>(null);

  const handleMigrateClearingAccounts = async () => {
    setLoading(true);
    setResult(null);
    
    try {
      await migrateExistingClearingAccounts();
      setResult({
        success: true,
        message: 'Successfully migrated clearing accounts to the new format.'
      });
    } catch (error) {
      console.error('Error migrating clearing accounts:', error);
      setResult({
        success: false,
        message: `Error migrating clearing accounts: ${error instanceof Error ? error.message : String(error)}`
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCreateClearingAccounts = async () => {
    setLoading(true);
    setResult(null);
    
    try {
      await createClearingAccountsForExistingMainAccounts();
      setResult({
        success: true,
        message: 'Successfully created clearing accounts for all main accounts.'
      });
    } catch (error) {
      console.error('Error creating clearing accounts:', error);
      setResult({
        success: false,
        message: `Error creating clearing accounts: ${error instanceof Error ? error.message : String(error)}`
      });
    } finally {
      setLoading(false);
    }
  };

  const handleFixClearingAccountReferences = async () => {
    setLoading(true);
    setResult(null);
    
    try {
      await fixClearingAccountReferences();
      setResult({
        success: true,
        message: 'Successfully fixed clearing account references.'
      });
    } catch (error) {
      console.error('Error fixing clearing account references:', error);
      setResult({
        success: false,
        message: `Error fixing clearing account references: ${error instanceof Error ? error.message : String(error)}`
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCreateDefaultAccountsReceivable = async () => {
    setLoading(true);
    setResult(null);
    
    try {
      await createDefaultAccountsReceivable();
      setResult({
        success: true,
        message: 'Successfully created default accounts receivable account.'
      });
    } catch (error) {
      console.error('Error creating default accounts receivable account:', error);
      setResult({
        success: false,
        message: `Error creating default accounts receivable account: ${error instanceof Error ? error.message : String(error)}`
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Admin Utilities
      </Typography>
      
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          GL Account Utilities
        </Typography>
        <Divider sx={{ mb: 2 }} />
        
        <List>
          <ListItem>
            <ListItemText 
              primary="Migrate Clearing Accounts" 
              secondary="Updates existing clearing accounts to use the new format that won't conflict with regular GL accounts."
            />
            <Button 
              variant="contained" 
              color="primary" 
              onClick={handleMigrateClearingAccounts}
              disabled={loading}
            >
              {loading ? <CircularProgress size={24} /> : 'Run Migration'}
            </Button>
          </ListItem>
          
          <ListItem>
            <ListItemText 
              primary="Create Missing Clearing Accounts" 
              secondary="Creates clearing accounts for any main accounts that don't have one."
            />
            <Button 
              variant="contained" 
              color="primary" 
              onClick={handleCreateClearingAccounts}
              disabled={loading}
            >
              {loading ? <CircularProgress size={24} /> : 'Create Accounts'}
            </Button>
          </ListItem>
          
          <ListItem>
            <ListItemText 
              primary="Fix Clearing Account References" 
              secondary="Ensures all clearing accounts have correct parent account references."
            />
            <Button 
              variant="contained" 
              color="primary" 
              onClick={handleFixClearingAccountReferences}
              disabled={loading}
            >
              {loading ? <CircularProgress size={24} /> : 'Fix References'}
            </Button>
          </ListItem>

          <ListItem>
            <ListItemText 
              primary="Create Accounts Receivable Account" 
              secondary="Creates a default accounts receivable account (1200000) if one doesn't exist. Required for invoice functionality."
            />
            <Button 
              variant="contained" 
              color="primary" 
              onClick={handleCreateDefaultAccountsReceivable}
              disabled={loading}
            >
              {loading ? <CircularProgress size={24} /> : 'Create AR Account'}
            </Button>
          </ListItem>
        </List>
        
        {result && (
          <Alert severity={result.success ? 'success' : 'error'} sx={{ mt: 2 }}>
            {result.message}
          </Alert>
        )}
      </Paper>
    </Box>
  );
};

export default AdminUtilities;
